/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-08-03 15:14:18
 **/

import { useEffect } from "react";
import { _T } from "@rfice/language";

import { Button, Column, Divider, Row, Typography, useToast } from "@/frameworks/designStuff/components";
import { UserItem } from "@/frameworks/core/components";
import { usePhoneCall, useSidebarActions } from "@/frameworks/core/hooks";
import { removeMeFromList } from "@/frameworks/core/utils";
import { RestAPIError } from "@/frameworks/libraries";
import { APIErrorCode, SidebarID } from "@/frameworks/core/commons";
import { useChatRoomActions } from "@/frameworks/core/chat";
import { useChatDataStore } from "@/modules/chat/store";

export default function GroupCallTabInfo({ callID }: { callID: string }) {
	const phoneCall = usePhoneCall();
	const busyUser = phoneCall.state.busyUsers;
	const callUsers = phoneCall.state.users(callID)[0];
	const callUserList = phoneCall.state.callees[0];
	const participatedUserIDList = removeMeFromList(
		callUsers
			? phoneCall.status(callID) === "ONGOING" || phoneCall.status(callID) === "END"
				? Object.values(callUsers)
						.filter(
							(user) =>
								![
									"HANGUP",
									"ENDED",
									"HANGUP_NO_RESPONSE",
									"REJECTED",
									"ENDED_BY_DEVICE_DISCONNECTED"
								].includes(user.status)
						)
						.map((user) => user.userId ?? "")
				: Object.values(callUsers).map((user) => user.userId ?? "")
			: callUserList.map((user) => user.userID)
	);

	const showFullSizeChat = useChatDataStore((state) => state.show_fullsize_chat);
	const sidebarActions = useSidebarActions();
	const chatRoomActions = useChatRoomActions();
	const toast = useToast();

	useEffect(() => {
		if (busyUser.length > 0) {
			toast.show({ type: "error", message: _T("상대방이 통화중입니다.") });
		}
	}, [busyUser]);

	const handleCallState = (userID: string, callID?: string) => {
		const isBusy = busyUser?.includes(userID);
		const userCallStatus = callUsers?.[userID]?.status;
		if (!callID) {
			return undefined;
		}
		if (isBusy || userCallStatus === "REJECTED") {
			return (
				<Typography typoColor="error_60_100" typoSize="Body6">
					{_T("통화 불가")}
				</Typography>
			);
		}
		if (userCallStatus === "ACCEPTED") {
			return _T("통화중");
		}
		const endedStatuses = ["ENDED", "ENDED_BY_DEVICE_DISCONNECTED", "HANGUP_NO_RESPONSE", "HANGUP"];
		if (endedStatuses.includes(userCallStatus)) {
			return undefined;
		}
		if (userCallStatus === "REQUESTED") {
			return _T("통화 연결 중");
		}
		return undefined;
	};

	const openDM = async (userList: string[]) => {
		try {
			if (showFullSizeChat) {
				await sidebarActions.hideAll("left");
				await sidebarActions.show(SidebarID.Chat);
			}
			const data = await chatRoomActions.dm.create({
				userIDs: [...userList]
			});
			if (!data) return;
			chatRoomActions.common.open({ channelID: data.id, type: "DM" });
		} catch (err) {
			const error = err as RestAPIError;
			switch (error.code) {
				case APIErrorCode.EXIST_SAME_CHAT_MEMBER: {
					const { roomId } = error.data.payload;
					chatRoomActions.common.open({ channelID: roomId, type: "DM" });
				}
			}
		}
	};
	return (
		<Column style={{ width: "100%" }}>
			<Row style={{ width: "100%", padding: "8px" }} horizonAlign="distribute">
				<Typography typoSize="Title1_B">
					{_T("{{count}}명 참여중", {
						count: participatedUserIDList?.length || 0
					})}
				</Typography>
				<Row gap={8} verticalAlign="center">
					<Typography typoSize="Body4">{_T("단체 다이렉트 메시지")}</Typography>
					<Button.Icon
						name="Chat0124"
						filled="transparent"
						size="Xlarge"
						onClick={() => openDM(participatedUserIDList)}
					/>
				</Row>
			</Row>
			<Divider type="line" />
			<Column style={{ width: "100%", paddingTop: "8px" }}>
				{[...new Set([...participatedUserIDList, ...busyUser])].map((userID) => (
					// UserItem email 항목 위치에 통화상태 넣는 작업 필요하여 email 필드에 작업했습니다.
					<UserItem id={userID || ""} key={userID || ""} email={handleCallState(userID || "", callID)}>
						<Row style={{ paddingRight: "8px" }}>
							<Button.Icon
								name="Chat0124"
								filled="transparent"
								size="Xlarge"
								onClick={() => openDM([userID])}
							/>
						</Row>
					</UserItem>
				))}
			</Column>
		</Column>
	);
}
